import {
  Flex,
  type FlexProps,
  FormCheckbox,
  type FormCheckboxProps,
  mergeSx,
} from '@shared/components';
import type { FieldValues } from 'react-hook-form';

export interface CustomCheckboxProps<TFieldValues extends FieldValues>
  extends FormCheckboxProps<TFieldValues> {
  endAdornment?: React.ReactNode;
  slotProps?: FormCheckboxProps<TFieldValues>['slotProps'] & {
    wrapper?: FlexProps;
  };
}

export default function CustomCheckbox<TFieldValues extends FieldValues = FieldValues>(
  props: Readonly<CustomCheckboxProps<TFieldValues>>,
) {
  const { endAdornment, slotProps, sx, ...restProps } = props;
  const {
    formControlLabel: formControlLabelProps,
    wrapper: wrapperProps,
    ...restSlotProps
  } = slotProps ?? {};
  const wrapperSx = mergeSx(
    (theme) => ({
      width: '100%',
      alignItems: 'center',
      minHeight: '52px',
      gap: theme.spacing(4),
      p: theme.spacing(2, 4),
    }),
    wrapperProps?.sx,
  );
  const additionalLabelSx = mergeSx(
    (theme) => ({
      flex: 1,
      mr: theme.spacing(0),
      gap: theme.spacing(4),
    }),
    formControlLabelProps?.sx,
  );
  const checkboxSx = mergeSx(
    (theme) => ({
      color: theme.palette.grey[400],
      p: theme.spacing(0),
    }),
    sx,
  );

  return (
    <Flex {...wrapperProps} sx={wrapperSx}>
      <FormCheckbox
        slotProps={{
          formControlLabel: {
            ...formControlLabelProps,
            sx: additionalLabelSx,
          },
          ...restSlotProps,
        }}
        sx={checkboxSx}
        {...restProps}
      />
      {endAdornment}
    </Flex>
  );
}
